// ─── settingsApi ─────────────────────────────────────────────────────────────
// Wrappers for the Settings → API and Settings → Streaming Method panels.
//
//   API Keys      → /api/api/apikey/        (project-scoped Apikey CRUD)
//   Hourly stats  → /api/api/apikey/stats/  ({ count, limit } for the panel header)
//   Streaming     → /api/data/streaming-method/  ({ project_id, method, custom_command_email })
//
// Same auth + cache-bust pattern as reportApi.js — Bearer token from
// localStorage, no-cache headers on GETs.

import axios from 'axios'

const API_URL         = import.meta.env.VITE_API_URL          || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH   = import.meta.env.VITE_API_BASE_PATH    || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY  || 'auth_token'

const authHeaders = () => {
    const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
}
const getHeaders = () => ({
    ...authHeaders(),
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma':        'no-cache',
    'Expires':       '0',
})

const url = (p) => `${API_URL}${API_BASE_PATH}${p}`

// ── API Keys ─────────────────────────────────────────────────────────────────
export async function listApiKeys(projectId) {
    const res = await axios.get(url('/api/apikey/'), {
        headers: getHeaders(),
        params:  { project_id: projectId, _t: Date.now() },
    })
    // DRF ModelViewSet list endpoint returns { count, next, previous, results }
    // when pagination is on, or a bare array otherwise. Normalise here.
    const data = res.data
    return Array.isArray(data) ? data : (data?.results || [])
}
export async function createApiKey({ projectId, name, allowIp = '' }) {
    const body = { project: projectId, name: name || '', allow_ip: allowIp || '' }
    const res = await axios.post(url('/api/apikey/'), body, { headers: authHeaders() })
    return res.data
}
export async function updateApiKey(id, { name, allowIp }) {
    const body = {}
    if (name     !== undefined) body.name     = name
    if (allowIp  !== undefined) body.allow_ip = allowIp
    const res = await axios.patch(url(`/api/apikey/${id}/`), body, { headers: authHeaders() })
    return res.data
}
export async function deleteApiKey(id) {
    const res = await axios.delete(url(`/api/apikey/${id}/`), { headers: authHeaders() })
    return res.data
}
export async function getApiKeyStats(projectId) {
    const res = await axios.get(url('/api/apikey/stats/'), {
        headers: getHeaders(),
        params:  { project_id: projectId, _t: Date.now() },
    })
    return res.data // { count, limit, window, project_id }
}

// ── Streaming Method ─────────────────────────────────────────────────────────
// Rows are keyed by (project_id, device_id). Pass `deviceId=null` to fetch
// the project-wide fallback row.
export async function getStreamingMethod(projectId, deviceId = null) {
    const res = await axios.get(url('/data/streaming-method/'), {
        headers: getHeaders(),
        params:  {
            project_id: projectId,
            ...(deviceId != null ? { device_id: deviceId } : {}),
            _t: Date.now(),
        },
    })
    return res.data
}
// `fields` is a partial — only the keys present are sent. Server clears
// the rest when `method` switches, so the client doesn't need to.
export async function updateStreamingMethod({ projectId, deviceId, ...fields }) {
    const body = {
        project_id: projectId,
        ...(deviceId != null ? { device_id: deviceId } : {}),
        ...fields,
    }
    const res = await axios.patch(url('/data/streaming-method/'), body, { headers: authHeaders() })
    return res.data
}
